import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleById, getCategoryBySlug, getRelatedArticles, testimonials } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-500 text-lg">文章不存在</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = getCategoryBySlug(article.category);
  const relatedArticles = getRelatedArticles(article, 4);
  const allTestimonials = testimonials;

  return (
    <>
      <Navbar />

      <main>
        {/* Breadcrumb */}
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-4">
          <nav className="flex items-center gap-2 text-xs md:text-sm" aria-label="面包屑导航">
            <Link to="/" className="text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer">首页</Link>
            <span className="text-foreground-400">/</span>
            {category && (
              <>
                <Link to={`/category/${category.slug}`} className="text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap">
                  {category.name}
                </Link>
                <span className="text-foreground-400">/</span>
              </>
            )}
            <span className="text-foreground-700 line-clamp-1">{article.title}</span>
          </nav>
        </div>

        {/* Article Header */}
        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-10">
          <div className="mb-6">
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                {category.name}
              </Link>
            )}

            <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-900 leading-tight mb-6">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-400 mb-6">
              <span>发布于 {article.publishDate}</span>
              <span>{article.views.toLocaleString()} 次阅读</span>
              <div className="flex flex-wrap gap-1.5">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 bg-background-100 text-foreground-500 rounded-full text-xs whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button className="px-5 py-2 text-sm font-medium bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                分享爆料
              </button>
              <button className="px-5 py-2 text-sm font-medium border border-foreground-300 text-foreground-700 rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                收藏本文
              </button>
            </div>
          </div>
        </article>

        {/* Featured Image */}
        <section className="w-full max-w-5xl mx-auto px-4 md:px-6 mb-10">
          <div className="rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-auto object-cover"
            />
          </div>
        </section>

        {/* Article Content */}
        <section className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-16">
          <div
            className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: article.content
                .replace(/<h3>/g, '<h3 class="font-heading text-xl font-bold text-foreground-900 mt-8 mb-4">')
                .replace(/<ul>/g, '<ul class="list-disc pl-6 space-y-2 mb-6 text-foreground-700">')
                .replace(/<li>/g, '<li class="text-sm">')
                .replace(/<p>/g, '<p class="mb-5 text-sm md:text-base leading-relaxed text-foreground-800">')
            }}
          />
        </section>

        {/* BreadcrumbList Schema */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://91yingba.cn/" },
              ...(category ? [{ "@type": "ListItem", "position": 2, "name": category.name, "item": `http://91yingba.cn/category/${category.slug}` }] : []),
              { "@type": "ListItem", "position": category ? 3 : 2, "name": article.title, "item": `http://91yingba.cn/article/${article.id}` }
            ]
          })
        }} />

        {/* Article Schema */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "datePublished": article.publishDate,
            "image": article.image,
            "author": {
              "@type": "Organization",
              "name": "五一吃瓜网"
            },
            "publisher": {
              "@type": "Organization",
              "name": "五一吃瓜网",
              "url": "http://91yingba.cn/"
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `http://91yingba.cn/article/${article.id}`
            }
          })
        }} />

        {/* Comment Section */}
        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="relative bg-foreground-900 rounded-2xl overflow-hidden">
              <div className="flex flex-col lg:flex-row">
                <div className="lg:w-1/3 relative">
                  <div className="relative h-80 lg:h-full -mb-12 lg:-mb-0 lg:-ml-12 z-10">
                    <img
                      src={allTestimonials[1]?.avatar || allTestimonials[0]?.avatar}
                      alt="网友头像"
                      className="w-full h-full object-cover object-top rounded-2xl lg:rounded-r-none"
                    />
                  </div>
                </div>
                <div className="lg:w-2/3 flex flex-col justify-center py-10 px-6 md:px-12 lg:pl-16">
                  <div className="mb-6">
                    <span className="text-5xl text-accent-400/40 font-serif leading-none">&ldquo;</span>
                  </div>
                  <p className="text-base md:text-lg text-background-50/90 leading-relaxed mb-6">
                    这个瓜真的太大了！五一吃瓜网总是能在第一时间给我们带来最新的爆料，比微博热搜还快，每天必刷。
                  </p>
                  <div className="mb-8">
                    <p className="text-sm font-semibold text-background-50">热心网友小王</p>
                    <p className="text-xs text-background-50/50">吃瓜群众</p>
                  </div>
                  <button className="self-start px-6 py-2.5 text-sm font-medium bg-secondary-500 text-background-50 rounded-full hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer">
                    发表评论
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full bg-background-100 py-16 md:py-20">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center">
                  同类<span className="text-accent-500"> 吃瓜推荐</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer hover:bg-background-100 transition-colors"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        title={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="inline-block px-2 py-0.5 text-xs font-medium bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">
                          关联
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-bold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h3>
                      <span className="text-xs text-foreground-400">{rel.publishDate}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="w-full bg-background-50 py-16 md:py-20">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-2xl mx-auto text-center">
              <div className="mb-10 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20smartphone%20with%20social%20media%20notifications%20floating%2C%20warm%20coral%20and%20dark%20olive%20color%20scheme%2C%20minimal%20clean%20aesthetic%2C%20contemporary%20editorial%20style%2C%20soft%20film%20grain%2C%20cozy%20atmosphere%2C%20subtle%20lighting&width=1200&height=600&seq=cta-article-page&orientation=landscape"
                  alt="关注五一吃瓜网获取最新爆料"
                  title="扫码关注五一吃瓜网"
                  className="w-full h-auto object-cover"
                />
              </div>

              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3 leading-snug">
                扫码关注<span className="font-normal italic text-foreground-500"> 五一吃瓜网</span>
              </h2>
              <p className="text-sm text-foreground-500 mb-8 max-w-md mx-auto">
                解锁隐藏瓜源，第一时间获取最新爆料
              </p>

              <Link
                to="/about"
                className="inline-block px-10 py-3.5 text-sm font-medium bg-foreground-900 text-background-50 rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
              >
                获取最新地址
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}